/**
 * Десктоп-раскладка (C6, макет docs/ux/voxel/13-desktop.png): поле по центру, слева постоянная панель героев
 * (вкладки C5 в режиме `panel`), справа колонка — серия (карточка окна C4), задания, «Поделиться» и место под
 * сундуки, друзей и рейтинг (v2 — скрыто). HUD и окна поверх боя центрируются по полю (`desktop.css`), а оболочка
 * сужает поле на ширину колонок (`--desk-inset-*`, `AppController.attachField`).
 */
import { dailyTasksClaimable } from "@newfungame/engine";
import { useStore, useT } from "../../app/hooks";
import type { DesktopViewProps } from "../../app/slots";
import { appStore, battleStore, openWindow } from "../../app/store";
import { FeedbackButton } from "../../app/FeedbackButton";
import { FullscreenButton } from "../../app/FullscreenButton";
import { LanguageButton } from "../../app/LanguageButton";
import { TelegramLoginButton, useTelegramLogin } from "../../app/TelegramLoginButton";
import { SoundToggle } from "../../audio/SoundToggle";
import { addMessages } from "../../i18n";
import { SIDE_UNLOCK } from "../hud/model";
import { Button } from "../kit";
import { StreakCard } from "../windows";
import { windowActions } from "../windows/actions";
import { desktopMessages, type DesktopKey } from "./messages";
import "./desktop.css";

addMessages(desktopMessages);

/** Волна для «Поделиться»: текущая в бою, иначе из забежки игрока. */
export function shareWave(battleWave: number | null, runWave: number | null): number {
  return battleWave ?? runWave ?? 1;
}

export function Desktop({ tabs }: DesktopViewProps) {
  const t = useT() as (k: DesktopKey) => string;
  const battleWave = useStore(battleStore, (s) => s?.wave.number ?? null);
  const runWave = useStore(appStore, (s) => s.player?.run.wave ?? null);
  const { login } = useTelegramLogin();
  const tasks = useStore(appStore, (s) => s.player?.tasks ?? null);
  const season = useStore(appStore, (s) => s.player?.season ?? null);
  const unclaimed = useStore(appStore, (s) => s.player?.league?.unclaimed ?? 0);
  const showTasks = useStore(appStore, (s) => (s.player?.bestWave ?? 0) >= (SIDE_UNLOCK.tasks ?? Infinity));
  return (
    <div class="desk kit" data-testid="desktop">
      <aside class="desk-col desk-col--left" data-testid="desk-left" aria-label={t("desktop.heroes")}>
        {tabs}
      </aside>
      <aside class="desk-col desk-col--right" data-testid="desk-right" aria-label={t("desktop.side")}>
        <SoundToggle />
        <LanguageButton />
        <FeedbackButton />
        <FullscreenButton />
        <StreakCard />
        {tasks && showTasks && (
          <Button
            variant="blue"
            class="desk-share desk-tasks"
            data-testid="desk-tasks"
            onClick={() => openWindow({ kind: "tasks" })}
          >
            {t("desktop.tasks")}
            {dailyTasksClaimable(tasks).length > 0 && <span class="kit-badge" data-testid="desk-tasks-badge" />}
          </Button>
        )}
        {season && showTasks && (
          <Button
            variant="blue"
            class="desk-share desk-tasks"
            data-testid="desk-season"
            onClick={() => openWindow({ kind: "season" })}
          >
            {t("desktop.season")}
          </Button>
        )}
        {showTasks && (
          <Button
            variant="blue"
            class="desk-share desk-tasks"
            data-testid="desk-league"
            onClick={() => openWindow({ kind: "league" })}
          >
            {t("desktop.league")}
            {unclaimed > 0 && <span class="kit-badge" data-testid="desk-league-badge" />}
          </Button>
        )}
        <Button
          variant="blue"
          class="desk-share"
          data-testid="desk-share"
          onClick={() => windowActions.get().share({ wave: shareWave(battleWave, runWave) })}
        >
          {t("desktop.share")}
        </Button>
        {login && (
          <section class="desk-login" data-testid="desk-login" aria-label={t("desktop.login")}>
            <p class="desk-login__hint">{t("desktop.loginHint")}</p>
            <TelegramLoginButton bot={login.bot} onAuth={login.onAuth} />
          </section>
        )}
        {/* v2: сундуки, друзья, рейтинг (макет 13) — место зарезервировано, пока скрыто */}
        <div class="desk-v2" data-testid="desk-v2" hidden />
      </aside>
    </div>
  );
}
